/**
 * 鼠标子应用「当前设备身份」单点：provider / stableId / 型号 deviceId / cid/mid/uuid。
 * 读法统一，避免菜单、休眠、传感器等各自猜 localStorage。
 */
import { useDeviceInfo, type DeviceInfo } from "../store/useDeviceInfo";
import { getSupportedDevicesSync } from "@eweadn-qiankun/shared-utils";

export type MouseSolutionProvider =
  | "beiying"
  | "jiangmeng"
  | "freq"
  | "zhanli"
  | "yxt";

export interface MouseIdentity {
  provider: MouseSolutionProvider;
  /** supportedDevices 中的 deviceId，如 z7、s9prov2 */
  deviceId: string;
  stableId: string | null;
  uuid: string | null;
  cid: number | null;
  mid: number | null;
}

interface MouseDeviceConfig {
  vendorId: number;
  productId: number;
  customerId?: number;
  moudleId?: number;
  deviceId?: string;
  /** 磁微动鼠标：触发点 / RT / 霍尔校准 */
  magneticMicroswitch?: boolean;
  /**
   * 磁微动：无线是否支持实时按压深度与按键校准。
   * 缺省 true（Alpha6 及后续）；老型号显式 false。
   */
  magneticMicroswitchWirelessTrigger?: boolean;
}

function readStoredSolutionRoviderName(): string | null {
  try {
    if (typeof window === "undefined") return null;
    const fromSession = window.sessionStorage.getItem(
      "eweadn:session:solutionRoviderName"
    );
    if (fromSession?.trim()) return fromSession.trim();
    const fromLocal = window.localStorage.getItem("solutionRoviderName");
    if (fromLocal?.trim()) return fromLocal.trim();
  } catch {
    /* ignore */
  }
  return null;
}

function readEquipmentDataSolutionRovider(): string | null {
  try {
    if (typeof window === "undefined") return null;
    const raw =
      window.sessionStorage.getItem("eweadn:session:equipmentData") ??
      window.localStorage.getItem("equipmentData");
    if (!raw) return null;
    const p = JSON.parse(raw)?.solutionRovider;
    return typeof p === "string" && p.trim() ? p.trim() : null;
  } catch {
    return null;
  }
}

function normalizeMouseProvider(
  name: string | null | undefined
): MouseSolutionProvider | null {
  if (!name) return null;
  const lower = String(name).trim().toLowerCase();
  if (
    lower === "beiying" ||
    lower === "freq" ||
    lower === "jiangmeng" ||
    lower === "zhanli" ||
    lower === "yxt"
  ) {
    return lower;
  }
  return null;
}

/** 从 localStorage / 入参解析当前型号 deviceId */
export function resolveMouseDeviceId(fallbackDeviceId?: string): string {
  if (fallbackDeviceId) return fallbackDeviceId;
  try {
    const saved = localStorage.getItem("currentDeviceId");
    if (saved) return saved;
    const raw = localStorage.getItem("equipmentData");
    if (raw) {
      const id = JSON.parse(raw)?.deviceId;
      if (typeof id === "string" && id) return id;
    }
  } catch {
    /* ignore */
  }
  return "";
}

/** 当前型号是否为磁微动（见 supportedDevices.magneticMicroswitch） */
export function mouseSupportsMagneticMicroswitch(deviceId?: string): boolean {
  const id = resolveMouseDeviceId(deviceId);
  if (!id) return false;
  return (getSupportedDevicesSync().mouse as MouseDeviceConfig[]).some(
    (row) => row.deviceId === id && row.magneticMicroswitch === true
  );
}

/** 当前型号是否为 Alpha6 */
export function mouseIsAlpha6(deviceId?: string): boolean {
  return resolveMouseDeviceId(deviceId).toLowerCase() === "alpha6";
}

/** 霍尔磁微动（S9V2 Master+ 等，非 Alpha6 电感） */
export function mouseIsHallMagneticTrigger(deviceId?: string): boolean {
  return mouseSupportsMagneticMicroswitch(deviceId) && !mouseIsAlpha6(deviceId);
}

/** Alpha6 专有：快速触发 / 触觉反馈 */
export function mouseSupportsTriggerMagneticExtras(deviceId?: string): boolean {
  return mouseIsAlpha6(deviceId);
}

/** 磁微动触发页实时按压深度（S9V2 Master+、Alpha6 等） */
export function mouseSupportsTriggerLivePressDepth(deviceId?: string): boolean {
  return mouseSupportsMagneticMicroswitch(deviceId);
}

/** 磁微动：按键声音（当前型号均不支持，保留协议字段） */
export function mouseSupportsTriggerButtonSound(_deviceId?: string): boolean {
  return false;
}

/** 磁微动按键校准（Alpha6 无此功能） */
export function mouseSupportsTriggerButtonCalibration(
  deviceId?: string
): boolean {
  if (!mouseSupportsMagneticMicroswitch(deviceId)) return false;
  return !mouseIsAlpha6(deviceId);
}

/**
 * 磁微动：无线是否支持实时按压深度与按键校准。
 * 设备表未标 false 时默认支持（Alpha6 及后续新型号）。
 */
export function mouseSupportsTriggerWirelessLiveAndCal(
  deviceId?: string
): boolean {
  const id = resolveMouseDeviceId(deviceId);
  if (!id || !mouseSupportsMagneticMicroswitch(deviceId)) return false;
  const row = (getSupportedDevicesSync().mouse as MouseDeviceConfig[]).find(
    (d) => d.deviceId === id && d.magneticMicroswitch === true
  );
  return row?.magneticMicroswitchWirelessTrigger !== false;
}

// function parseMouseSolutionProvider(
//   value: unknown
// ): MouseSolutionProvider | null {
//   if (
//     value === "beiying" ||
//     value === "freq" ||
//     value === "jiangmeng" ||
//     value === "zhanli"
//   ) {
//     return value;
//   }
//   return null;
// }

export function getMouseProvider(): MouseSolutionProvider {
  const fromName = normalizeMouseProvider(readStoredSolutionRoviderName());
  if (fromName) return fromName;
  const fromEq = normalizeMouseProvider(readEquipmentDataSolutionRovider());
  if (fromEq) return fromEq;
  try {
    if (typeof window !== "undefined") {
      const sid =
        window.sessionStorage.getItem("eweadn:session:mouseCurrentStableId") ??
        window.localStorage.getItem("mouseCurrentStableId");
      if (sid?.startsWith("yxt-")) return "yxt";
      if (sid?.startsWith("beiying-")) return "beiying";
    }
  } catch {
    /* ignore */
  }
  return "jiangmeng";
}

/** 与 SleepGuard 原 equipmentData.is4G 一致，用于无线/休眠旁路判断 */
export function getMouseWirelessFromEquipment(): boolean {
  try {
    const raw = localStorage.getItem("equipmentData");
    if (raw) return JSON.parse(raw)?.is4G === true;
  } catch {
    /* ignore */
  }
  return false;
}

function normalizeStableIdInner(
  stableId: string | null,
  provider: MouseSolutionProvider
): string | null {
  if (!stableId) return null;
  if (provider === "beiying") {
    return stableId.startsWith("beiying-") ? stableId : null;
  }
  if (provider === "yxt") {
    return stableId.startsWith("yxt-") ? stableId : null;
  }
  return stableId.startsWith("mouse-") ? stableId : null;
}

/** 事件 detail 等外部传入的 stableId 与当前方案对齐 */
export function normalizeMouseStableId(stableId: string | null): string | null {
  return normalizeStableIdInner(stableId, getMouseProvider());
}

export function getCurrentMouseStableId(): string | null {
  if (typeof window === "undefined") return null;
  let raw: string | null = null;
  try {
    raw =
      window.sessionStorage.getItem("eweadn:session:mouseCurrentStableId") ??
      window.localStorage.getItem("mouseCurrentStableId");
  } catch {
    raw = localStorage.getItem("mouseCurrentStableId");
  }
  return normalizeStableIdInner(raw, getMouseProvider());
}

function inferDeviceIdFromCidMid(deviceInfo: DeviceInfo): string {
  const cid = deviceInfo.info?.cid;
  const mid = deviceInfo.info?.mid;
  if (!cid || !mid) return "";
  const mouseDevice = (
    getSupportedDevicesSync().mouse as MouseDeviceConfig[]
  ).find((device) => device.customerId === cid && device.moudleId === mid);
  return mouseDevice?.deviceId || "";
}

export function buildMouseIdentity(deviceInfo: DeviceInfo): MouseIdentity {
  const provider = getMouseProvider();
  const stableId = getCurrentMouseStableId();
  const uuid =
    typeof deviceInfo.uuid === "string" && deviceInfo.uuid
      ? deviceInfo.uuid
      : null;
  const cid =
    typeof deviceInfo.info?.cid === "number" ? deviceInfo.info.cid : null;
  const mid =
    typeof deviceInfo.info?.mid === "number" ? deviceInfo.info.mid : null;

  let deviceId = "";
  try {
    const savedDeviceId =
      typeof window !== "undefined"
        ? localStorage.getItem("currentDeviceId")
        : null;
    if (savedDeviceId) {
      const matched = (
        getSupportedDevicesSync().mouse as MouseDeviceConfig[]
      ).find((d) => d.deviceId === savedDeviceId);
      deviceId = matched?.deviceId || "";
    }
  } catch {
    /* ignore */
  }
  if (!deviceId && deviceInfo.modelId) {
    deviceId = deviceInfo.modelId;
  }
  if (!deviceId) {
    deviceId = inferDeviceIdFromCidMid(deviceInfo);
  }

  return { provider, deviceId, stableId, uuid, cid, mid };
}

export function getCurrentMouseIdentity(): MouseIdentity {
  return buildMouseIdentity(useDeviceInfo.getState().deviceInfo);
}
